import React, { useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import {
  LoginWrapper,
  FormContainer,
  Form,
  Title,
  Button,
  LeftContainer,
  LoginLogo,
  ResetPasswordButton,
} from "../Login/Login.styles";
import AppInput from "@components/AppInput";
import LoginLogoSVG from "@assets/img/login-logo.svg";
import PasswordIcon from "@assets/img/lock-icon.svg";
import EyeIcon from "@assets/img/eye-icon-yellow.svg";
import EyeIconLine from "@assets/img/eye-line.svg";
import { useNavigate, useSearchParams } from "react-router-dom";
import { theme } from "@src/theme";
import { useAppDispatch } from "@src/redux/hooks";
import { resetPasswordThunk } from "@src/redux/thunks/authThunks";
import { useAuthState } from "@src/hooks/useAuth";
import LoginLeftContainer from "../Login/LoginLeftContainer";
import Spinner from "@src/components/Spinner";
import { clearError, resetPasswordStatus } from "@src/redux/slices/authSlice";

interface ResetPasswordForm {
  password: string;
  repeatPassword: string;
}

const ResetPasswordForm: React.FC = () => {
  const [showPassword, setShowPassword] = React.useState({
    password: false,
    repeat: false,
  });
  // get code from params
  const [searchParams] = useSearchParams();
  const code = searchParams.get("code");

  const {
    errorMessage,
    loading: loadingLogin,
    resetPasswordStatus: requestStatus,
  } = useAuthState();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<ResetPasswordForm>({
    resolver: yupResolver(
      Yup.object().shape({
        password: Yup.string()
          .min(8, "Password must be at least 8 characters")
          .required("Password is required"),
        repeatPassword: Yup.string()
          .oneOf([Yup.ref("password"), undefined], "Passwords must match")
          .required("Repeat Password is required"),
      })
    ),
  });

  const onSubmitSendEmail = (data: ResetPasswordForm): void => {
    if (!code) return;
    dispatch(resetPasswordThunk({ password: data.password, code }));
  };

  const goToLogin = (): void => {
    navigate("/login");
  };

  useEffect(() => {
    return () => {
      dispatch(clearError());
      dispatch(resetPasswordStatus(null));
    };
  }, []);

  return (
    <LoginWrapper>
      <FormContainer>
        <Form onSubmit={handleSubmit(onSubmitSendEmail)}>
          <LoginLogo src={LoginLogoSVG} />
          <Title>RESET PASSWORD</Title>
          <div style={{ minHeight: 50 }}>
            {loadingLogin && <Spinner small />}
            {requestStatus === "error" && (
              <p style={{ color: theme.colors.red }}>{errorMessage}</p>
            )}
            {requestStatus === "success" && (
              <p style={{ color: theme.colors.green }}>
                Password reset successfully
              </p>
            )}
          </div>
          <Controller
            name="password"
            control={control}
            render={({ field }) => (
              <AppInput
                label="PLEASE ENTER YOUR PASSWORD"
                control={control}
                errors={errors}
                placeholder="Enter your password"
                type={showPassword.password ? "text" : "password"}
                {...field}
                icon={<img src={PasswordIcon} alt="Password" />}
                iconRight={
                  <img
                    onClick={() =>
                      setShowPassword({
                        ...showPassword,
                        password: !showPassword.password,
                      })
                    }
                    src={showPassword.password ? EyeIcon : EyeIconLine}
                    alt="Show Password"
                  />
                }
              />
            )}
          />
          <Controller
            name="repeatPassword"
            control={control}
            render={({ field }) => (
              <AppInput
                label="PLEASE REPEAT YOUR PASSWORD"
                control={control}
                errors={errors}
                placeholder="Repeat your password"
                type={showPassword.repeat ? "text" : "password"}
                {...field}
                icon={<img src={PasswordIcon} alt="Password" />}
                iconRight={
                  <img
                    onClick={() =>
                      setShowPassword({
                        ...showPassword,
                        repeat: !showPassword.repeat,
                      })
                    }
                    src={showPassword.repeat ? EyeIcon : EyeIconLine}
                    alt="Show Password"
                  />
                }
              />
            )}
          />
          <ResetPasswordButton type="button" onClick={goToLogin}>
            BACK TO LOGIN
          </ResetPasswordButton>
          <Button type="submit">RESET PASSWORD</Button>
        </Form>
      </FormContainer>
      <LeftContainer>
        <LoginLeftContainer />
      </LeftContainer>
    </LoginWrapper>
  );
};

export default ResetPasswordForm;
